/**
 * src/domain/moderation/community-jury-engine.ts
 *
 * 7-Member Anti-Scam Community Jury State Machine & Quorum Engine.
 */

import { ChannelVisaEngine } from '../identity/channel-visa-engine';

export type DisputeCategory =
  | 'PHISHING_OR_MALICIOUS_LINK'
  | 'AGE_GATE_BYPASS_FORGERY'
  | 'IMPERSONATION_FRAUD'
  | 'ABUSIVE_HARASSMENT'
  | 'P2P_COMMERCE_NON_DELIVERY';

export type DisputePhase = 'COMMIT' | 'REVEAL' | 'SETTLED' | 'DISMISSED';
export type JurorVoteChoice = 'GUILTY' | 'ACQUITTED';

export interface JurorCandidate {
  did: string;
  sovereignScore: number;
  isSlashed: boolean;
  vouchedForDids: string[];
  vouchedByDids: string[];
}

export interface DisputeIncident {
  disputeId: string;
  reporterDid: string;
  defendantDid: string;
  category: DisputeCategory;
  evidenceUri: string;
  evidencePayloadHash: string;
  reportedAt: number;
  commitDeadline: number;
  revealDeadline: number;
  phase: DisputePhase;
  selectedJurorDids: string[];
  commitments: Record<string, string>; // jurorDid -> commitmentHash
  reveals: Record<string, { choice: JurorVoteChoice; salt: string }>;
  verdict?: JurorVoteChoice;
  settledAt?: number;
}

/**
 * Universal synchronous SHA-256 digest function (browser & Node.js compatible).
 */
function sha256Sync(str: string): string {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const rotr = (n: number, x: number) => (x >>> n) | (x << (32 - n));
  const utf8 = unescape(encodeURIComponent(str));
  const words: number[] = [];
  for (let i = 0; i < utf8.length; i++) {
    words[i >> 2] |= (utf8.charCodeAt(i) & 0xff) << (24 - (i % 4) * 8);
  }
  words[utf8.length >> 2] |= 0x80 << (24 - (utf8.length % 4) * 8);
  words[(((utf8.length + 8) >> 6) << 4) + 15] = utf8.length * 8;

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const w = new Array(64);
  for (let i = 0; i < words.length; i += 16) {
    for (let j = 0; j < 16; j++) w[j] = words[i + j] | 0;
    for (let j = 16; j < 64; j++) {
      const s0 = rotr(7, w[j - 15]) ^ rotr(18, w[j - 15]) ^ (w[j - 15] >>> 3);
      const s1 = rotr(17, w[j - 2]) ^ rotr(19, w[j - 2]) ^ (w[j - 2] >>> 10);
      w[j] = (w[j - 16] + s0 + w[j - 7] + s1) | 0;
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;

    for (let j = 0; j < 64; j++) {
      const s1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + K[j] + w[j]) | 0;
      const s0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) | 0;

      h = g; g = f; f = e; e = (d + temp1) | 0;
      d = c; c = b; b = a; a = (temp1 + temp2) | 0;
    }

    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }

  const toHex = (n: number) => ('00000000' + (n >>> 0).toString(16)).slice(-8);
  return toHex(h0) + toHex(h1) + toHex(h2) + toHex(h3) + toHex(h4) + toHex(h5) + toHex(h6) + toHex(h7);
}

export class CommunityJuryEngine {
  static readonly REQUIRED_JURORS = 7;
  static readonly SUPERMAJORITY_QUORUM = 5;
  static readonly MIN_JUROR_SCORE = 500;
  static readonly COMMIT_WINDOW_MS = 24 * 60 * 60 * 1000;
  static readonly REVEAL_WINDOW_MS = 24 * 60 * 60 * 1000;
  static readonly JUROR_BOUNTY_TOKENS = 5;

  private static incidents: Map<string, DisputeIncident> = new Map();

  /**
   * Deterministic SHA-256 hash helper
   */
  static sha256(value: string): string {
    return sha256Sync(value);
  }

  /**
   * Generates a commitment hash for blind voting
   */
  static generateCommitment(choice: JurorVoteChoice, salt: string, disputeId: string): string {
    return this.sha256(`${choice}:${salt}:${disputeId}`);
  }

  /**
   * Files a formal dispute incident and deterministically drafts 7 jurors
   */
  static fileDispute(params: {
    disputeId: string;
    reporterDid: string;
    defendantDid: string;
    category: DisputeCategory;
    evidenceUri: string;
    evidencePayload: string;
    blockRootHash: string;
    eligiblePool: JurorCandidate[];
  }): DisputeIncident {
    if (params.reporterDid === params.defendantDid) {
      throw new Error('Self-reporting disputes are invalid.');
    }

    const payloadHash = this.sha256(params.evidencePayload);
    const now = Date.now();

    // 1. Conflict of Interest Filtering
    const nonConflictedCandidates = params.eligiblePool.filter((c) => {
      if (c.did === params.reporterDid || c.did === params.defendantDid) return false;
      if (c.isSlashed || c.sovereignScore < this.MIN_JUROR_SCORE) return false;

      // Cannot be voucher or vouchee of either party
      const isRelatedToReporter =
        c.vouchedForDids.includes(params.reporterDid) ||
        c.vouchedByDids.includes(params.reporterDid);
      const isRelatedToDefendant =
        c.vouchedForDids.includes(params.defendantDid) ||
        c.vouchedByDids.includes(params.defendantDid);

      return !isRelatedToReporter && !isRelatedToDefendant;
    });

    if (nonConflictedCandidates.length < this.REQUIRED_JURORS) {
      throw new Error(
        `Insufficient non-conflicted candidates for jury drafting. Required ${this.REQUIRED_JURORS}, found ${nonConflictedCandidates.length}.`
      );
    }

    // 2. Deterministic VRF selection using block hash and dispute parameters
    const seed = this.sha256(`${params.disputeId}:${params.blockRootHash}:${params.reporterDid}`);
    const selectedJurorDids = this.draftJurors(nonConflictedCandidates, seed, this.REQUIRED_JURORS);

    const incident: DisputeIncident = {
      disputeId: params.disputeId,
      reporterDid: params.reporterDid,
      defendantDid: params.defendantDid,
      category: params.category,
      evidenceUri: params.evidenceUri,
      evidencePayloadHash: payloadHash,
      reportedAt: now,
      commitDeadline: now + this.COMMIT_WINDOW_MS,
      revealDeadline: now + this.COMMIT_WINDOW_MS + this.REVEAL_WINDOW_MS,
      phase: 'COMMIT',
      selectedJurorDids,
      commitments: {},
      reveals: {},
    };

    this.incidents.set(params.disputeId, incident);
    return incident;
  }

  /**
   * Deterministic sampling of N unique candidates using PRNG seeded from hash
   */
  private static draftJurors(candidates: JurorCandidate[], seed: string, count: number): string[] {
    const pool = [...candidates];
    const selected: string[] = [];
    let currentHash = seed;

    while (selected.length < count && pool.length > 0) {
      currentHash = this.sha256(currentHash);
      const indexInt = parseInt(currentHash.substring(0, 8), 16);
      const pickedIndex = indexInt % pool.length;
      selected.push(pool[pickedIndex].did);
      pool.splice(pickedIndex, 1);
    }

    return selected;
  }

  /**
   * Juror submits vote commitment (Blind Phase)
   */
  static submitCommitment(disputeId: string, jurorDid: string, commitmentHash: string): void {
    const incident = this.incidents.get(disputeId);
    if (!incident) throw new Error(`Dispute ${disputeId} not found.`);
    if (incident.phase !== 'COMMIT') throw new Error('Commitment window closed.');
    if (!incident.selectedJurorDids.includes(jurorDid)) {
      throw new Error(`DID ${jurorDid} is not a summoned juror for this dispute.`);
    }

    incident.commitments[jurorDid] = commitmentHash;

    // Advance early if all 7 committed
    if (Object.keys(incident.commitments).length === this.REQUIRED_JURORS) {
      incident.phase = 'REVEAL';
    }
  }

  /**
   * Juror reveals their vote with the original salt (Reveal Phase)
   */
  static submitReveal(
    disputeId: string,
    jurorDid: string,
    choice: JurorVoteChoice,
    salt: string
  ): void {
    const incident = this.incidents.get(disputeId);
    if (!incident) throw new Error(`Dispute ${disputeId} not found.`);
    const commitment = incident.commitments[jurorDid];
    if (!commitment) {
      throw new Error(`No commitment recorded for juror ${jurorDid}.`);
    }

    // Preimage check: Hash(choice + salt + disputeId) == commitment
    const expected = this.generateCommitment(choice, salt, disputeId);
    if (expected !== commitment) {
      throw new Error('Preimage verification failed. Vote and salt do not match commitment.');
    }

    if (incident.phase !== 'REVEAL') {
      // Auto-advance if past commit deadline
      if (Date.now() > incident.commitDeadline && incident.phase === 'COMMIT') {
        incident.phase = 'REVEAL';
      } else {
        throw new Error('Dispute is not in REVEAL phase.');
      }
    }

    incident.reveals[jurorDid] = { choice, salt };

    // Settle immediately if all 7 revealed
    if (Object.keys(incident.reveals).length === this.REQUIRED_JURORS) {
      this.settleDispute(disputeId);
    }
  }

  /**
   * Quorum tally and penalty cascade settlement
   */
  static settleDispute(disputeId: string): {
    verdict: JurorVoteChoice;
    guiltyVotes: number;
    acquittedVotes: number;
    slashedStakeTotal?: number;
    bountiedJurors: string[];
  } {
    const incident = this.incidents.get(disputeId);
    if (!incident) throw new Error(`Dispute ${disputeId} not found.`);

    let guiltyVotes = 0;
    let acquittedVotes = 0;
    const revealedJurors = Object.entries(incident.reveals);

    for (const [, { choice }] of revealedJurors) {
      if (choice === 'GUILTY') guiltyVotes++;
      if (choice === 'ACQUITTED') acquittedVotes++;
    }

    let verdict: JurorVoteChoice;
    let slashedStakeTotal = 0;
    const bountiedJurors: string[] = [];

    // Requires 5/7 Supermajority to convict
    if (guiltyVotes >= this.SUPERMAJORITY_QUORUM) {
      verdict = 'GUILTY';

      // 1. Trigger automated 4-step penalty cascade
      const cascadeResult = ChannelVisaEngine.triggerPenaltyCascade({
        violatorDid: incident.defendantDid,
        reason: `Convicted by 7-Member Community Jury on dispute ${disputeId} (${incident.category})`,
      });
      slashedStakeTotal = cascadeResult.slashedTokens;

      // 2. Identify honest consensus voters to award bounties
      for (const [jurorDid, { choice }] of revealedJurors) {
        if (choice === 'GUILTY') {
          bountiedJurors.push(jurorDid);
        }
      }
    } else {
      verdict = 'ACQUITTED';
      for (const [jurorDid, { choice }] of revealedJurors) {
        if (choice === 'ACQUITTED') {
          bountiedJurors.push(jurorDid);
        }
      }
    }

    incident.verdict = verdict;
    incident.phase = 'SETTLED';
    incident.settledAt = Date.now();

    return {
      verdict,
      guiltyVotes,
      acquittedVotes,
      slashedStakeTotal,
      bountiedJurors,
    };
  }

  static getIncident(disputeId: string): DisputeIncident | undefined {
    return this.incidents.get(disputeId);
  }

  static clear(): void {
    this.incidents.clear();
  }
}

export const communityJuryEngine = CommunityJuryEngine;
